import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useToast } from "./use-toast";
import { addMonths, differenceInCalendarMonths, endOfMonth, format, startOfMonth, subMonths } from "date-fns";
import { sendOwnerPush } from "@/lib/owner-push";
import { isNativeIosApp } from "@/lib/platform";

type Appointment = Tables<"appointments">;
type AppointmentInsert = TablesInsert<"appointments">;
type AppointmentUpdate = TablesUpdate<"appointments">;

/** Same 6-char format as public booking (avoid 0/O, 1/I). Used for walk-in/admin-created appointments. */
function generateConfirmationCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let result = "";
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

interface AppointmentWithDetails extends Appointment {
  customer?: Tables<"customers">;
  service?: Tables<"services">;
  staffMember?: Tables<"staff">;
}

export function useAppointments() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const isNativeIos = isNativeIosApp();
  const [appointments, setAppointments] = useState<AppointmentWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAppointments = useCallback(async () => {
    if (!business) {
      setAppointments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("appointments")
        .select(`
          *,
          customer:customers(*),
          service:services(*),
          staffMember:staff(*)
        `)
        .eq("business_id", business.id)
        .order("start_datetime", { ascending: true });

      if (fetchError) throw fetchError;
      setAppointments(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const createAppointment = async (appointmentData: Omit<AppointmentInsert, "business_id">) => {
    if (!business) throw new Error("No business found");

    // Friendly client-side limit check (DB triggers also enforce)
    const planId = (business as any).plan_id || "starter";
    const { data: plan, error: planError } = await supabase
      .from("subscription_plans")
      .select("booking_limit_monthly")
      .eq("id", planId)
      .maybeSingle();
    if (planError) throw planError;
    const bookingLimit = (plan as any)?.booking_limit_monthly ?? null;

    if (bookingLimit != null) {
      const createdAt = new Date();

      // Match DB enforcement: billing-cycle month anchored to subscription_last_paid_at when available.
      // Fallback: calendar month. Usage is counted when the booking is created.
      const anchorRaw = (business as any)?.subscription_last_paid_at;
      let periodStart: Date;
      let periodEnd: Date;

      if (anchorRaw) {
        const anchor = new Date(anchorRaw);
        let months = differenceInCalendarMonths(createdAt, anchor);
        periodStart = addMonths(anchor, months);
        if (periodStart > createdAt) {
          periodStart = subMonths(periodStart, 1);
        }
        periodEnd = addMonths(periodStart, 1);
      } else {
        periodStart = startOfMonth(createdAt);
        // Keep consistent with server (month end is exclusive); client query uses <= so use endOfMonth for safety.
        periodEnd = endOfMonth(createdAt);
      }

      const periodStartISO = periodStart.toISOString();
      const periodEndISO = periodEnd.toISOString();

      const { data: existing, error: countError } = await supabase
        .from("appointments")
        .select("id")
        .eq("business_id", business.id)
        .gte("created_at", periodStartISO)
        .lte("created_at", periodEndISO);
      if (countError) throw countError;

      const current = existing?.length || 0;
      if (current >= bookingLimit) {
        toast({
          title: "Booking limit reached",
          description: isNativeIos
            ? "Please contact Buukd support for help adding more bookings."
            : `Your plan allows up to ${bookingLimit} bookings per billing period. Upgrade to add more.`,
          variant: "destructive",
        });
        throw new Error("Booking limit reached");
      }
    }

    // Assign a booking code (same as client-side booking) so walk-ins can use pay-remaining-balance flow
    let data: any = null;
    let lastError: any = null;
    for (let attempt = 0; attempt < 5; attempt++) {
      const code = generateConfirmationCode();
      const { data: inserted, error } = await supabase
        .from("appointments")
        .insert({
          ...appointmentData,
          business_id: business.id,
          confirmation_code: code,
        } as any)
        .select()
        .single();

      if (!error) {
        data = inserted;
        break;
      }
      lastError = error;
      if (error?.code === "23505") continue; // unique violation on confirmation_code
      const msg = (error as any)?.message || "Failed to create appointment";
      if (msg.toLowerCase().includes("booking limit reached")) {
        toast({
          title: "Booking limit reached",
          description: isNativeIos
            ? "Please contact Buukd support for help adding more bookings."
            : "Upgrade your plan to create more bookings for that month.",
          variant: "destructive",
        });
      }
      throw error;
    }

    if (!data) {
      throw lastError || new Error("Failed to create appointment. Please try again.");
    }

    void sendOwnerPush({
      businessId: business.id,
      title: "New appointment",
      body: "A new appointment has been added to your calendar.",
      url: "/app/appointments",
      data: {
        context: "appointment_created",
        context_id: data.id,
        source: appointmentData.source || "admin",
      },
    });

    await fetchAppointments();
    return data;
  };

  const updateAppointment = async (id: string, appointmentData: AppointmentUpdate) => {
    // Check if appointment has a final status before updating
    const appointment = appointments.find(apt => apt.id === id);
    if (appointment) {
      const finalStatuses = ["completed", "cancelled", "no_show"];
      if (finalStatuses.includes(appointment.status)) {
        // If trying to change status from final status, prevent it
        if (appointmentData.status && appointmentData.status !== appointment.status) {
          throw new Error("Cannot change status of appointments with final status (completed, cancelled, or no_show)");
        }
        // If trying to update anything else on a final status appointment, prevent it
        if (Object.keys(appointmentData).some(key => key !== "status")) {
          throw new Error("Cannot edit appointments with final status (completed, cancelled, or no_show)");
        }
      }
    }

    const previousStart = appointment?.start_datetime || null;
    const previousStatus = appointment?.status || null;

    const { data, error } = await supabase
      .from("appointments")
      .update(appointmentData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    const nextStatus = (data as any)?.status || appointmentData.status || previousStatus;
    if (appointmentData.status && previousStatus && nextStatus && previousStatus !== nextStatus) {
      void sendOwnerPush({
        businessId: business?.id,
        title: "Appointment status changed",
        body: `Appointment status changed from ${previousStatus.replaceAll("_", " ")} to ${String(nextStatus).replaceAll("_", " ")}.`,
        url: "/app/appointments",
        data: {
          context: "appointment_status_changed",
          context_id: id,
          previous_status: previousStatus,
          new_status: nextStatus,
        },
      });
    }

    const nextStart = (data as any)?.start_datetime || appointmentData.start_datetime || previousStart;
    if (
      previousStart &&
      nextStart &&
      String(previousStart) !== String(nextStart)
    ) {
      void sendOwnerPush({
        businessId: business?.id,
        title: "Appointment rescheduled",
        body: `An appointment was moved to ${format(new Date(nextStart), "EEE, MMM d 'at' h:mm a")}.`,
        url: "/app/appointments",
        data: {
          context: "appointment_rescheduled",
          context_id: id,
          previous_start_datetime: previousStart,
          new_start_datetime: nextStart,
        },
      });
    }

    await fetchAppointments();
    return data;
  };

  const deleteAppointment = async (id: string) => {
    const { error } = await supabase.from("appointments").delete().eq("id", id);
    if (error) throw error;
    await fetchAppointments();
  };

  return {
    appointments,
    loading,
    error,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    refetch: fetchAppointments,
  };
}
